import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, Plus, Trash2, RefreshCw, Check, X, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = 'https://baustellenmgmt.mas123.de/api';

const DEFAULT_COLORS = [
  { value: '#3b82f6', label: 'Blau' },
  { value: '#10b981', label: 'Grün' },
  { value: '#f59e0b', label: 'Orange' },
  { value: '#ef4444', label: 'Rot' },
  { value: '#8b5cf6', label: 'Violett' },
  { value: '#ec4899', label: 'Pink' },
];

const DEFAULT_RECHNUNGSANALYSE_SETTINGS = {
  analyzer: 'invoice2data',
  llm_api_url: 'https://api.openai.com/v1/chat/completions',
  llm_api_key: '',
  llm_model: 'gpt-4o-mini',
  llm_prompt: '',
  llm_items_prompt: '',
  llm_template_prompt: '',
  llm_template_analysis_prompt: '',
  llm_template_generation_prompt: '',
  invoice_filename_pattern: '{{date}}_{{issuer}}_{{invoice_number}}_{{amount}}',
};

const DEFAULT_APP_SETTINGS = {
  company_name: '',
  logo: null,
  favicon: null,
  ods_template: null,
  primary_color: '#0ea5e9',
  description_max_length: 40,
  backup_to_nextcloud_enabled: false,
  backup_nextcloud_path: '',
  backup_interval: 'daily',
  backup_hour: 2,
  backup_retention_copies: 10,
};

const SETTINGS_TABS = [
  { id: 'stundenkalender', label: 'Stundenkalender' },
  { id: 'branding', label: 'Branding' },
  { id: 'nextcloud', label: 'Nextcloudanbindung' },
  { id: 'rechnungen', label: 'Rechnungen' },
  { id: 'backup', label: 'Backup und Wiederherstellung' },
];

function listToEditorText(values) {
  if (!Array.isArray(values)) return '';
  return values.filter((value) => String(value || '').trim()).join('\n');
}

function editorTextToList(value) {
  return String(value || '')
    .split(/\r?\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function jsonToEditorText(value, fallback = {}) {
  try {
    return JSON.stringify(value ?? fallback, null, 2);
  } catch (error) {
    return JSON.stringify(fallback, null, 2);
  }
}

function parseEditorJson(value, fallback = {}) {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  return JSON.parse(raw);
}

function compareByLabelDe(a, b) {
  return String(a || '').localeCompare(String(b || ''), 'de', { sensitivity: 'base' });
}

function toRechnungspositionsTypUi(entry = {}) {
  return {
    ...entry,
    beschreibung: entry.beschreibung || '',
    farbe: entry.farbe || '',
    sortierung: entry.sortierung ?? 100,
    aktiv: entry.aktiv ?? true,
    keywords_text: listToEditorText(entry.keywords_json),
    einheiten_text: listToEditorText(entry.einheiten_json),
    _saveState: 'idle',
    _saveError: '',
  };
}

function toLieferantenRegelUi(entry = {}) {
  return {
    ...entry,
    issuer_name: entry.issuer_name || '',
    aktiv: entry.aktiv ?? true,
    beschreibung: entry.beschreibung || '',
    prioritaet: entry.prioritaet ?? 100,
    match_keywords_text: listToEditorText(entry.match_keywords_json),
    exclude_keywords_text: listToEditorText(entry.exclude_keywords_json),
    preferred_template_ids_json: Array.isArray(entry.preferred_template_ids_json) ? entry.preferred_template_ids_json : [],
    type_hints_text: jsonToEditorText(entry.type_hints_json, {}),
    match_hints_text: jsonToEditorText(entry.match_hints_json, {}),
    review_thresholds_text: jsonToEditorText(entry.review_hint_thresholds_json, {}),
    _saveState: 'idle',
    _saveError: '',
  };
}

function SettingsView({ token }) {
  const [activeTab, setActiveTab] = useState('stundenkalender');
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [newCalendar, setNewCalendar] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupTestBusy, setBackupTestBusy] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const restoreInputRef = useRef(null);
  const [tagOptionen, setTagOptionen] = useState([]);
  const [editingTagOption, setEditingTagOption] = useState(null);
  const [newTagOption, setNewTagOption] = useState(null);
  const [calendarDefaultWeeks, setCalendarDefaultWeeks] = useState(() => {
    const stored = localStorage.getItem('calendarDefaultWeeks');
    const parsed = stored ? parseInt(stored, 10) : 2;
    return Number.isNaN(parsed) ? 2 : Math.max(1, parsed);
  });
  const [baustellenStatsDefaultWeeks, setBaustellenStatsDefaultWeeks] = useState(() => {
    const stored = localStorage.getItem('baustellenStatsDefaultWeeks');
    const parsed = stored ? parseInt(stored, 10) : 2;
    return Number.isNaN(parsed) ? 2 : Math.max(1, parsed);
  });
  const [calendarShowEmployees, setCalendarShowEmployees] = useState(() => {
    const stored = localStorage.getItem('calendarShowEmployees');
    return stored ? stored === 'true' : true;
  });
  const [calendarShowSubunternehmer, setCalendarShowSubunternehmer] = useState(() => {
    const stored = localStorage.getItem('calendarShowSubunternehmer');
    return stored ? stored === 'true' : true;
  });
  const [baustellenStatsShowPerson, setBaustellenStatsShowPerson] = useState(() => {
    const stored = localStorage.getItem('baustellenStatsShowPerson');
    return stored ? stored === 'true' : false;
  });
  const [urlaubColor, setUrlaubColor] = useState(() => localStorage.getItem('urlaubColor') || '#e0f2fe');
  const [krankColor, setKrankColor] = useState(() => localStorage.getItem('krankColor') || '#fee2e2');
  const [appSettings, setAppSettings] = useState(DEFAULT_APP_SETTINGS);
  const [appSettingsFiles, setAppSettingsFiles] = useState({ logo: null, favicon: null, ods_template: null });
  const [arbeitszeitraeume, setArbeitszeitraeume] = useState([]);
  const [editingArbeitszeit, setEditingArbeitszeit] = useState(null);
  const [newArbeitszeit, setNewArbeitszeit] = useState(null);
  const [stundenverrechnungGlobal, setStundenverrechnungGlobal] = useState([]);
  const [editingStundenverrechnungGlobal, setEditingStundenverrechnungGlobal] = useState(null);
  const [newStundenverrechnungGlobal, setNewStundenverrechnungGlobal] = useState(null);
  const [rechnungsaufschlagGlobal, setRechnungsaufschlagGlobal] = useState([]);
  const [editingRechnungsaufschlagGlobal, setEditingRechnungsaufschlagGlobal] = useState(null);
  const [newRechnungsaufschlagGlobal, setNewRechnungsaufschlagGlobal] = useState(null);
  const [nextcloudSettings, setNextcloudSettings] = useState({
    webdav_url: '',
    username: '',
    password: '',
    base_path: '',
    rechnungen_path: '',
    aktiv: false
  });
  const [showNextcloudPicker, setShowNextcloudPicker] = useState(false);
  const [nextcloudPickerLoading, setNextcloudPickerLoading] = useState(false);
  const [nextcloudPickerError, setNextcloudPickerError] = useState('');
  const [nextcloudPickerCurrentPath, setNextcloudPickerCurrentPath] = useState('');
  const [nextcloudPickerDirs, setNextcloudPickerDirs] = useState([]);
  const [nextcloudPickerTarget, setNextcloudPickerTarget] = useState('rechnungen');
  const [rechnungsanalyseSettings, setRechnungsanalyseSettings] = useState({
    analyzer: 'invoice2data',
    llm_api_url: 'https://api.openai.com/v1/chat/completions',
    llm_api_key: '',
    llm_model: 'gpt-4o-mini',
    llm_prompt: '',
    llm_items_prompt: '',
    llm_template_prompt: '',
    llm_template_analysis_prompt: '',
    llm_template_generation_prompt: '',
    invoice_filename_pattern: '{{date}}_{{issuer}}_{{invoice_number}}_{{amount}}',
  });
  const [rechnungTemplates, setRechnungTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState(null);
  const [rechnungspositionsTypen, setRechnungspositionsTypen] = useState([]);
  const [rechnungspositionsTypenLoading, setRechnungspositionsTypenLoading] = useState(false);
  const [rechnungspositionsTypenError, setRechnungspositionsTypenError] = useState('');
  const [newRechnungspositionsTyp, setNewRechnungspositionsTyp] = useState(null);
  const [lieferantenRegeln, setLieferantenRegeln] = useState([]);
  const [lieferantenRegelnLoading, setLieferantenRegelnLoading] = useState(false);
  const [lieferantenRegelnError, setLieferantenRegelnError] = useState('');
  const [newLieferantenRegel, setNewLieferantenRegel] = useState(null);

  const loadCalendars = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/kalender/external-calendars/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCalendars(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Kalender:', error);
      alert('Fehler beim Laden der Kalender');
    }
    setLoading(false);
  }, [token]);

  const loadAppSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/app-settings/`);
      setAppSettings({ ...DEFAULT_APP_SETTINGS, ...(response.data || {}) });
    } catch (error) {
      console.error('Fehler beim Laden der App-Settings:', error);
    }
  }, []);

  const loadNextcloudSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/nextcloud-settings/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNextcloudSettings(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Nextcloud-Settings:', error);
    }
  }, [token]);

  const loadTagOptionen = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/stunden/optionen/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTagOptionen(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Tag-Optionen:', error);
    }
  }, [token]);

  const loadArbeitszeitraeume = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/stunden/arbeitszeitraeume/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setArbeitszeitraeume(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Arbeitszeiträume:', error);
    }
  }, [token]);

  const loadRechnungsanalyseSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/rechnungsanalyse-settings/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRechnungsanalyseSettings({ ...DEFAULT_RECHNUNGSANALYSE_SETTINGS, ...(response.data || {}) });
    } catch (error) {
      console.error('Fehler beim Laden der Rechnungsanalyse-Settings:', error);
    }
  }, [token]);

  const loadStundenverrechnungGlobal = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/stundenverrechnung-global/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStundenverrechnungGlobal(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der globalen Stundenverrechnung:', error);
    }
  }, [token]);

  const loadRechnungsaufschlagGlobal = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/rechnungsaufschlag-global/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRechnungsaufschlagGlobal(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden des globalen Rechnungsaufschlags:', error);
    }
  }, [token]);

  const loadRechnungTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/rechnung-templates/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRechnungTemplates(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Rechnungstemplates:', error);
    }
  }, [token]);

  const loadRechnungspositionsTypen = useCallback(async () => {
    setRechnungspositionsTypenLoading(true);
    setRechnungspositionsTypenError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/core/rechnungspositions-typen/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const entries = response.data.results || response.data || [];
      setRechnungspositionsTypen(Array.isArray(entries) ? entries.map(toRechnungspositionsTypUi) : []);
    } catch (error) {
      console.error('Fehler beim Laden der Rechnungspositions-Typen:', error);
      setRechnungspositionsTypenError(
        error.response?.data?.detail || 'Fehler beim Laden der Rechnungspositions-Typen.'
      );
      setRechnungspositionsTypen([]);
    }
    setRechnungspositionsTypenLoading(false);
  }, [token]);

  const loadLieferantenRegeln = useCallback(async () => {
    setLieferantenRegelnLoading(true);
    setLieferantenRegelnError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/core/rechnungs-lieferantenregeln/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const entries = response.data.results || response.data || [];
      setLieferantenRegeln(Array.isArray(entries) ? entries.map(toLieferantenRegelUi) : []);
    } catch (error) {
      console.error('Fehler beim Laden der Lieferantenregeln:', error);
      setLieferantenRegeln([]);
      setLieferantenRegelnError(error.response?.data?.detail || 'Fehler beim Laden der Lieferantenregeln.');
    }
    setLieferantenRegelnLoading(false);
  }, [token]);

  useEffect(() => {
    loadCalendars();
    loadTagOptionen();
    loadAppSettings();
    loadNextcloudSettings();
    loadArbeitszeitraeume();
    loadStundenverrechnungGlobal();
    loadRechnungsaufschlagGlobal();
    loadRechnungsanalyseSettings();
    loadRechnungTemplates();
    loadRechnungspositionsTypen();
    loadLieferantenRegeln();
  }, [
    loadCalendars,
    loadTagOptionen,
    loadAppSettings,
    loadNextcloudSettings,
    loadArbeitszeitraeume,
    loadStundenverrechnungGlobal,
    loadRechnungsaufschlagGlobal,
    loadRechnungsanalyseSettings,
    loadRechnungTemplates,
    loadRechnungspositionsTypen,
    loadLieferantenRegeln,
  ]);

  useEffect(() => {
    localStorage.setItem('calendarDefaultWeeks', String(calendarDefaultWeeks));
  }, [calendarDefaultWeeks]);

  useEffect(() => {
    localStorage.setItem('baustellenStatsDefaultWeeks', String(baustellenStatsDefaultWeeks));
  }, [baustellenStatsDefaultWeeks]);

  useEffect(() => {
    localStorage.setItem('calendarShowEmployees', String(calendarShowEmployees));
  }, [calendarShowEmployees]);

  useEffect(() => {
    localStorage.setItem('calendarShowSubunternehmer', String(calendarShowSubunternehmer));
  }, [calendarShowSubunternehmer]);

  useEffect(() => {
    localStorage.setItem('baustellenStatsShowPerson', String(baustellenStatsShowPerson));
  }, [baustellenStatsShowPerson]);

  useEffect(() => {
    localStorage.setItem('urlaubColor', urlaubColor);
  }, [urlaubColor]);

  useEffect(() => {
    localStorage.setItem('krankColor', krankColor);
  }, [krankColor]);

  async function saveCalendar(calendar) {
    try {
      if (calendar.id) {
        // Update existing
        await axios.put(
          `${API_BASE_URL}/kalender/external-calendars/${calendar.id}/`,
          calendar,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        // Create new
        await axios.post(
          `${API_BASE_URL}/kalender/external-calendars/`,
          calendar,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      setEditingCalendar(null);
      setNewCalendar(null);
      loadCalendars();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      if (error.response?.data) {
        alert(JSON.stringify(error.response.data));
      } else {
        alert('Fehler beim Speichern des Kalenders');
      }
    }
  }

  async function saveTagOption(option) {
    try {
      const payload = {
        name: option.name,
        kurz: option.kurz,
        farbe: option.farbe || '#22c55e',
        aktiv: option.aktiv ?? true,
        berechnungstyp: option.berechnungstyp === 'ARBEITSZEIT' ? 'ARBEITSZEIT' : 'FEHLZEIT',
        arbeitszeit_automatisch_berechnen: true,
        aktiv_mo: option.aktiv_mo ?? true,
        aktiv_di: option.aktiv_di ?? true,
        aktiv_mi: option.aktiv_mi ?? true,
        aktiv_do: option.aktiv_do ?? true,
        aktiv_fr: option.aktiv_fr ?? true,
        aktiv_sa: option.aktiv_sa ?? true,
        aktiv_so: option.aktiv_so ?? true,
        beginn_mo: option.beginn_mo || '',
        ende_mo: option.ende_mo || '',
        pause_mo: option.pause_mo || '',
        beginn_di: option.beginn_di || '',
        ende_di: option.ende_di || '',
        pause_di: option.pause_di || '',
        beginn_mi: option.beginn_mi || '',
        ende_mi: option.ende_mi || '',
        pause_mi: option.pause_mi || '',
        beginn_do: option.beginn_do || '',
        ende_do: option.ende_do || '',
        pause_do: option.pause_do || '',
        beginn_fr: option.beginn_fr || '',
        ende_fr: option.ende_fr || '',
        pause_fr: option.pause_fr || '',
        beginn_sa: option.beginn_sa || '',
        ende_sa: option.ende_sa || '',
        pause_sa: option.pause_sa || '',
        beginn_so: option.beginn_so || '',
        ende_so: option.ende_so || '',
        pause_so: option.pause_so || '',
      };

      if (option.id) {
        await axios.patch(
          `${API_BASE_URL}/stunden/optionen/${option.id}/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/stunden/optionen/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      setEditingTagOption(null);
      setNewTagOption(null);
      loadTagOptionen();
    } catch (error) {
      console.error('Fehler beim Speichern der Tag-Option:', error);
      if (error.response?.data) {
        alert(JSON.stringify(error.response.data));
      } else {
        alert('Fehler beim Speichern der Tag-Option');
      }
    }
  }

  async function saveAppSettings(options = {}) {
    const {
      keys = null,
      includeFiles = true,
      showSuccessAlert = false,
      successMessage = 'Einstellungen gespeichert.',
    } = options;
    try {
      const formData = new FormData();
      const shouldInclude = (key) => !keys || keys.includes(key);

      if (shouldInclude('company_name')) {
        formData.append('company_name', appSettings.company_name || '');
      }
      if (shouldInclude('primary_color')) {
        formData.append('primary_color', appSettings.primary_color || '#0ea5e9');
      }
      if (shouldInclude('description_max_length')) {
        formData.append('description_max_length', String(Math.max(1, parseInt(appSettings.description_max_length, 10) || 40)));
      }
      if (shouldInclude('backup_to_nextcloud_enabled')) {
        formData.append('backup_to_nextcloud_enabled', appSettings.backup_to_nextcloud_enabled ? 'true' : 'false');
      }
      if (shouldInclude('backup_nextcloud_path')) {
        formData.append('backup_nextcloud_path', appSettings.backup_nextcloud_path || '');
      }
      if (shouldInclude('backup_interval')) {
        formData.append('backup_interval', appSettings.backup_interval || 'daily');
      }
      if (shouldInclude('backup_hour')) {
        formData.append('backup_hour', String(Math.max(0, Math.min(23, parseInt(appSettings.backup_hour, 10) || 0))));
      }
      if (shouldInclude('backup_retention_copies')) {
        formData.append('backup_retention_copies', String(Math.max(1, parseInt(appSettings.backup_retention_copies, 10) || 1)));
      }
      if (includeFiles && shouldInclude('logo') && appSettingsFiles.logo) {
        formData.append('logo', appSettingsFiles.logo);
      }
      if (includeFiles && shouldInclude('favicon') && appSettingsFiles.favicon) {
        formData.append('favicon', appSettingsFiles.favicon);
      }
      if (includeFiles && shouldInclude('ods_template') && appSettingsFiles.ods_template) {
        formData.append('ods_template', appSettingsFiles.ods_template);
      }
      const response = await axios.patch(`${API_BASE_URL}/core/app-settings/`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAppSettings({ ...DEFAULT_APP_SETTINGS, ...(response.data || {}) });
      if (includeFiles) {
        setAppSettingsFiles({ logo: null, favicon: null, ods_template: null });
      }
      if (showSuccessAlert) {
        alert(successMessage);
      }
      return response.data;
    } catch (error) {
      console.error('Fehler beim Speichern der App-Settings:', error);
      alert('Fehler beim Speichern der App-Settings');
      throw error;
    }
  }

  async function saveNextcloudSettings(options = {}) {
    const {
      showSuccessAlert = false,
      successMessage = 'Nextcloud-Einstellungen gespeichert.',
    } = options;
    try {
      const response = await axios.patch(`${API_BASE_URL}/core/nextcloud-settings/`, nextcloudSettings, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNextcloudSettings(response.data);
      if (showSuccessAlert) {
        alert(successMessage);
      }
      return response.data;
    } catch (error) {
      console.error('Fehler beim Speichern der Nextcloud-Settings:', error);
      alert('Fehler beim Speichern der Nextcloud-Settings');
      throw error;
    }
  }

  async function saveRechnungsanalyseSettings(options = {}) {
    const {
      showSuccessAlert = false,
      successMessage = 'Rechnungsanalyse-Einstellungen gespeichert.',
    } = options;
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/core/rechnungsanalyse-settings/`,
        rechnungsanalyseSettings,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setRechnungsanalyseSettings({ ...DEFAULT_RECHNUNGSANALYSE_SETTINGS, ...(response.data || {}) });
      if (showSuccessAlert) {
        alert(successMessage);
      }
      return response.data;
    } catch (error) {
      console.error('Fehler beim Speichern der Rechnungsanalyse-Settings:', error);
      alert('Fehler beim Speichern der Rechnungsanalyse-Settings');
      throw error;
    }
  }

  async function saveStundenkalenderTab() {
    localStorage.setItem('calendarDefaultWeeks', String(calendarDefaultWeeks));
    localStorage.setItem('baustellenStatsDefaultWeeks', String(baustellenStatsDefaultWeeks));
    localStorage.setItem('calendarShowEmployees', String(calendarShowEmployees));
    localStorage.setItem('calendarShowSubunternehmer', String(calendarShowSubunternehmer));
    localStorage.setItem('baustellenStatsShowPerson', String(baustellenStatsShowPerson));
    localStorage.setItem('urlaubColor', urlaubColor);
    localStorage.setItem('krankColor', krankColor);
    await saveAppSettings({
      keys: ['description_max_length'],
      includeFiles: false,
      showSuccessAlert: true,
      successMessage: 'Stundenkalender-Einstellungen gespeichert.',
    });
  }

  async function saveBrandingTab() {
    await saveAppSettings({
      keys: ['company_name', 'primary_color', 'logo', 'favicon', 'ods_template'],
      includeFiles: true,
      showSuccessAlert: true,
      successMessage: 'Branding gespeichert.',
    });
  }

  async function saveNextcloudTab() {
    await saveNextcloudSettings({
      showSuccessAlert: true,
      successMessage: 'Nextcloudanbindung gespeichert.',
    });
  }

  async function saveRechnungenTab() {
    await saveNextcloudSettings();
    await saveRechnungsanalyseSettings({
      showSuccessAlert: true,
      successMessage: 'Rechnungs-Einstellungen gespeichert.',
    });
  }

  async function saveBackupTab() {
    await saveAppSettings({
      keys: ['backup_to_nextcloud_enabled', 'backup_nextcloud_path', 'backup_interval', 'backup_hour', 'backup_retention_copies'],
      includeFiles: false,
      showSuccessAlert: true,
      successMessage: 'Backup-Einstellungen gespeichert.',
    });
  }

  function startNewTemplate() {
    setNewTemplate({
      name: '',
      issuer: '',
      yaml_content: '',
      aktiv: true
    });
  }

  async function saveTemplate(template) {
    try {
      if (template.id) {
        await axios.patch(
          `${API_BASE_URL}/core/rechnung-templates/${template.id}/`,
          template,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/core/rechnung-templates/`,
          template,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      setEditingTemplate(null);
      setNewTemplate(null);
      loadRechnungTemplates();
    } catch (error) {
      console.error('Fehler beim Speichern des Rechnungstemplates:', error);
      alert('Fehler beim Speichern des Rechnungstemplates');
    }
  }

  function startNewRechnungspositionsTyp() {
    setNewRechnungspositionsTyp(toRechnungspositionsTypUi({
      key: '',
      label: '',
      beschreibung: '',
      farbe: '',
      sortierung: 100,
      aktiv: true,
      keywords_json: [],
      einheiten_json: [],
      match_config_json: {},
    }));
  }

  function updateRechnungspositionsTypLocal(id, patch) {
    setRechnungspositionsTypen((prev) => prev.map((entry) => (
      entry.id === id
        ? { ...entry, ...patch, _saveState: patch._saveState || 'unsaved', _saveError: patch._saveError ?? '' }
        : entry
    )));
  }

  function buildRechnungspositionsTypPayload(entry) {
    const sortierung = parseInt(entry.sortierung, 10);
    return {
      key: String(entry.key || '').trim(),
      label: String(entry.label || '').trim(),
      beschreibung: String(entry.beschreibung || '').trim(),
      farbe: String(entry.farbe || '').trim(),
      sortierung: Number.isNaN(sortierung) ? 100 : sortierung,
      aktiv: entry.aktiv ?? true,
      keywords_json: editorTextToList(entry.keywords_text),
      einheiten_json: editorTextToList(entry.einheiten_text),
      match_config_json: entry.match_config_json || {},
    };
  }

  async function saveRechnungspositionsTyp(entry, options = {}) {
    const payload = buildRechnungspositionsTypPayload(entry);
    if (!payload.key) {
      const message = 'Key darf nicht leer sein.';
      if (entry.id) {
        updateRechnungspositionsTypLocal(entry.id, { _saveState: 'error', _saveError: message });
      } else {
        setNewRechnungspositionsTyp((prev) => ({ ...prev, _saveState: 'error', _saveError: message }));
      }
      return;
    }
    if (!payload.label) {
      const message = 'Label darf nicht leer sein.';
      if (entry.id) {
        updateRechnungspositionsTypLocal(entry.id, { _saveState: 'error', _saveError: message });
      } else {
        setNewRechnungspositionsTyp((prev) => ({ ...prev, _saveState: 'error', _saveError: message }));
      }
      return;
    }

    if (entry.id) {
      updateRechnungspositionsTypLocal(entry.id, { _saveState: 'saving', _saveError: '' });
    } else {
      setNewRechnungspositionsTyp((prev) => ({ ...prev, _saveState: 'saving', _saveError: '' }));
    }

    try {
      const response = entry.id
        ? await axios.patch(
          `${API_BASE_URL}/core/rechnungspositions-typen/${entry.id}/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        )
        : await axios.post(
          `${API_BASE_URL}/core/rechnungspositions-typen/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

      const savedEntry = toRechnungspositionsTypUi(response.data || payload);
      savedEntry._saveState = 'saved';
      savedEntry._saveError = '';

      if (entry.id) {
        setRechnungspositionsTypen((prev) => prev.map((current) => (
          current.id === entry.id ? savedEntry : current
        )));
      } else {
        setRechnungspositionsTypen((prev) => [...prev, savedEntry].sort((a, b) => {
          if ((a.sortierung ?? 0) !== (b.sortierung ?? 0)) return (a.sortierung ?? 0) - (b.sortierung ?? 0);
          return String(a.label || '').localeCompare(String(b.label || ''), 'de');
        }));
        setNewRechnungspositionsTyp(null);
      }

      if (options.reload !== false) {
        loadRechnungspositionsTypen();
      }
    } catch (error) {
      console.error('Fehler beim Speichern des Rechnungspositions-Typs:', error);
      const message = error.response?.data?.key?.[0]
        || error.response?.data?.label?.[0]
        || error.response?.data?.detail
        || 'Fehler beim Speichern des Rechnungspositions-Typs.';
      if (entry.id) {
        updateRechnungspositionsTypLocal(entry.id, { _saveState: 'error', _saveError: message });
      } else {
        setNewRechnungspositionsTyp((prev) => ({ ...prev, _saveState: 'error', _saveError: message }));
      }
    }
  }

  function startNewLieferantenRegel() {
    setNewLieferantenRegel(toLieferantenRegelUi({
      issuer_name: '',
      aktiv: true,
      beschreibung: '',
      prioritaet: 100,
      match_keywords_json: [],
      exclude_keywords_json: [],
      preferred_template_ids_json: [],
      type_hints_json: {},
      match_hints_json: {},
      review_hint_thresholds_json: {},
    }));
  }

  function updateLieferantenRegelLocal(id, patch) {
    setLieferantenRegeln((prev) => prev.map((entry) => (
      entry.id === id
        ? { ...entry, ...patch, _saveState: patch._saveState || 'unsaved', _saveError: patch._saveError ?? '' }
        : entry
    )));
  }

  function buildLieferantenRegelPayload(entry) {
    return {
      issuer_name: String(entry.issuer_name || '').trim(),
      aktiv: entry.aktiv ?? true,
      beschreibung: String(entry.beschreibung || '').trim(),
      prioritaet: parseInt(entry.prioritaet, 10) || 100,
      match_keywords_json: editorTextToList(entry.match_keywords_text),
      exclude_keywords_json: editorTextToList(entry.exclude_keywords_text),
      preferred_template_ids_json: (entry.preferred_template_ids_json || []).map((value) => Number(value)).filter((value) => !Number.isNaN(value)),
      type_hints_json: parseEditorJson(entry.type_hints_text, {}),
      match_hints_json: parseEditorJson(entry.match_hints_text, {}),
      review_hint_thresholds_json: parseEditorJson(entry.review_thresholds_text, {}),
    };
  }

  async function saveLieferantenRegel(entry) {
    const localError = (message) => {
      if (entry.id) {
        updateLieferantenRegelLocal(entry.id, { _saveState: 'error', _saveError: message });
      } else {
        setNewLieferantenRegel((prev) => ({ ...prev, _saveState: 'error', _saveError: message }));
      }
    };

    let payload;
    try {
      payload = buildLieferantenRegelPayload(entry);
    } catch (error) {
      localError(`Ungültiges JSON: ${error.message}`);
      return;
    }
    if (!payload.issuer_name) {
      localError('Lieferantenname darf nicht leer sein.');
      return;
    }

    if (entry.id) {
      updateLieferantenRegelLocal(entry.id, { _saveState: 'saving', _saveError: '' });
    } else {
      setNewLieferantenRegel((prev) => ({ ...prev, _saveState: 'saving', _saveError: '' }));
    }

    try {
      const response = entry.id
        ? await axios.patch(`${API_BASE_URL}/core/rechnungs-lieferantenregeln/${entry.id}/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        : await axios.post(`${API_BASE_URL}/core/rechnungs-lieferantenregeln/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      const savedEntry = toLieferantenRegelUi(response.data || payload);
      savedEntry._saveState = 'saved';
      if (entry.id) {
        setLieferantenRegeln((prev) => prev.map((current) => current.id === entry.id ? savedEntry : current));
      } else {
        setLieferantenRegeln((prev) => [...prev, savedEntry].sort((a, b) => (b.prioritaet ?? 0) - (a.prioritaet ?? 0) || compareByLabelDe(a.issuer_name, b.issuer_name)));
        setNewLieferantenRegel(null);
      }
      loadLieferantenRegeln();
    } catch (error) {
      console.error('Fehler beim Speichern der Lieferantenregel:', error);
      localError(
        error.response?.data?.issuer_name?.[0]
        || error.response?.data?.detail
        || 'Fehler beim Speichern der Lieferantenregel.'
      );
    }
  }

  async function deleteTemplate(id) {
    if (!window.confirm('Template wirklich löschen?')) {
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/core/rechnung-templates/${id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadRechnungTemplates();
    } catch (error) {
      console.error('Fehler beim Löschen des Templates:', error);
      alert('Fehler beim Löschen des Templates');
    }
  }

  async function loadNextcloudDirectories(path = '') {
    setNextcloudPickerLoading(true);
    setNextcloudPickerError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/core/nextcloud-directories/`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { path }
      });
      setNextcloudPickerCurrentPath(response.data.current_path || '');
      setNextcloudPickerDirs(response.data.directories || []);
    } catch (error) {
      console.error('Fehler beim Laden der Nextcloud-Verzeichnisse:', error);
      setNextcloudPickerError(error.response?.data?.error || 'Fehler beim Laden der Verzeichnisse');
      setNextcloudPickerDirs([]);
    }
    setNextcloudPickerLoading(false);
  }

  async function openRechnungenPathPicker() {
    setNextcloudPickerTarget('rechnungen');
    setShowNextcloudPicker(true);
    await loadNextcloudDirectories(nextcloudSettings.rechnungen_path || '');
  }

  async function openBackupPathPicker() {
    setNextcloudPickerTarget('backup');
    setShowNextcloudPicker(true);
    await loadNextcloudDirectories(appSettings.backup_nextcloud_path || '');
  }

  function closeNextcloudPicker() {
    setShowNextcloudPicker(false);
    setNextcloudPickerError('');
  }

  function goToNextcloudParent() {
    if (!nextcloudPickerCurrentPath) return;
    const parts = nextcloudPickerCurrentPath.split('/').filter(Boolean);
    parts.pop();
    loadNextcloudDirectories(parts.join('/'));
  }

  function selectRechnungenPath() {
    setNextcloudSettings((prev) => ({ ...prev, rechnungen_path: nextcloudPickerCurrentPath || '' }));
    closeNextcloudPicker();
  }

  function selectBackupPath() {
    setAppSettings((prev) => ({ ...prev, backup_nextcloud_path: nextcloudPickerCurrentPath || '' }));
    closeNextcloudPicker();
  }

  function confirmNextcloudPathSelection() {
    if (nextcloudPickerTarget === 'backup') {
      selectBackupPath();
      return;
    }
    selectRechnungenPath();
  }

  async function saveArbeitszeit(zeitraum) {
    try {
      if (zeitraum.id) {
        await axios.put(
          `${API_BASE_URL}/stunden/arbeitszeitraeume/${zeitraum.id}/`,
          zeitraum,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/stunden/arbeitszeitraeume/`,
          zeitraum,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      setEditingArbeitszeit(null);
      setNewArbeitszeit(null);
      loadArbeitszeitraeume();
    } catch (error) {
      console.error('Fehler beim Speichern des Arbeitszeitraums:', error);
      alert('Fehler beim Speichern des Arbeitszeitraums');
    }
  }

  async function deleteArbeitszeit(id) {
    if (!window.confirm('Arbeitszeitraum wirklich löschen?')) {
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/stunden/arbeitszeitraeume/${id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadArbeitszeitraeume();
    } catch (error) {
      console.error('Fehler beim Löschen des Arbeitszeitraums:', error);
      alert('Fehler beim Löschen des Arbeitszeitraums');
    }
  }

  async function saveStundenverrechnungGlobal(eintrag) {
    try {
      const payload = {
        wert: eintrag.wert,
        gueltig_ab: eintrag.gueltig_ab,
      };
      if (eintrag.id) {
        await axios.put(
          `${API_BASE_URL}/baustellen/stundenverrechnung-global/${eintrag.id}/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/baustellen/stundenverrechnung-global/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      setEditingStundenverrechnungGlobal(null);
      setNewStundenverrechnungGlobal(null);
      loadStundenverrechnungGlobal();
    } catch (error) {
      console.error('Fehler beim Speichern der globalen Stundenverrechnung:', error);
      alert('Fehler beim Speichern der globalen Stundenverrechnung');
    }
  }

  async function deleteStundenverrechnungGlobal(id) {
    if (!window.confirm('Diesen Eintrag wirklich löschen?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/baustellen/stundenverrechnung-global/${id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadStundenverrechnungGlobal();
    } catch (error) {
      console.error('Fehler beim Löschen der globalen Stundenverrechnung:', error);
      alert('Fehler beim Löschen der globalen Stundenverrechnung');
    }
  }

  async function saveRechnungsaufschlagGlobal(eintrag) {
    try {
      const payload = {
        wert: eintrag.wert,
        gueltig_ab: eintrag.gueltig_ab,
      };
      if (eintrag.id) {
        await axios.put(
          `${API_BASE_URL}/baustellen/rechnungsaufschlag-global/${eintrag.id}/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/baustellen/rechnungsaufschlag-global/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      setEditingRechnungsaufschlagGlobal(null);
      setNewRechnungsaufschlagGlobal(null);
      loadRechnungsaufschlagGlobal();
    } catch (error) {
      console.error('Fehler beim Speichern des globalen Rechnungsaufschlags:', error);
      alert('Fehler beim Speichern des globalen Rechnungsaufschlags');
    }
  }

  async function deleteRechnungsaufschlagGlobal(id) {
    if (!window.confirm('Diesen Eintrag wirklich löschen?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/baustellen/rechnungsaufschlag-global/${id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadRechnungsaufschlagGlobal();
    } catch (error) {
      console.error('Fehler beim Löschen des globalen Rechnungsaufschlags:', error);
      alert('Fehler beim Löschen des globalen Rechnungsaufschlags');
    }
  }

  async function deleteTagOption(id) {
    if (!window.confirm('Tag-Option wirklich löschen?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/stunden/optionen/${id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadTagOptionen();
    } catch (error) {
      console.error('Fehler beim Löschen der Tag-Option:', error);
      alert('Fehler beim Löschen der Tag-Option');
    }
  }

  async function deleteCalendar(id) {
    if (!window.confirm('Kalender wirklich löschen? Alle importierten Events werden gelöscht.')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/kalender/external-calendars/${id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadCalendars();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen des Kalenders');
    }
  }

  async function syncCalendar(id) {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/kalender/external-calendars/${id}/sync/`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.stats) {
        const { created, updated, skipped, errors } = response.data.stats;
        alert(
          `Kalender synchronisiert!\n\n` +
          `✓ ${created} neue Events\n` +
          `✓ ${updated} aktualisierte Events\n` +
          `- ${skipped} übersprungen\n` +
          (errors && errors.length > 0 ? `⚠ ${errors.length} Fehler` : '')
        );
      } else {
        alert(response.data.message || 'Kalender synchronisiert');
      }

      loadCalendars();
    } catch (error) {
      console.error('Fehler beim Synchronisieren:', error);
      const errorMsg = error.response?.data?.error || 'Fehler beim Synchronisieren';
      alert(errorMsg);
    }
    setLoading(false);
  }

  async function downloadBackup() {
    setBackupBusy(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/core/backup/`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const filename = response.headers['content-disposition']
        ?.split('filename=')[1]
        ?.replace(/"/g, '')
        || 'baustellenmgmt-backup.json';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Fehler beim Backup:', error);
      alert('Backup konnte nicht erstellt werden.');
    }
    setBackupBusy(false);
  }

  async function testNextcloudBackup() {
    setBackupTestBusy(true);
    try {
      await saveAppSettings();
      const response = await axios.post(
        `${API_BASE_URL}/core/backup/test-nextcloud/`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const deletedCount = response.data?.deleted_count || 0;
      const deletedText = deletedCount > 0 ? `\nGelöschte alte Sicherungen: ${deletedCount}` : '';
      alert(`Backup erfolgreich auf Nextcloud gespeichert.\nDatei: ${response.data?.filename || '-'}${deletedText}`);
    } catch (error) {
      console.error('Fehler beim Test-Backup:', error);
      alert(error.response?.data?.error || 'Test-Backup konnte nicht erstellt werden.');
    }
    setBackupTestBusy(false);
  }

  function triggerRestore() {
    if (restoreBusy) return;
    restoreInputRef.current?.click();
  }

  async function handleRestoreFile(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!window.confirm('Wiederherstellung startet. Alle aktuellen Daten werden ersetzt. Fortfahren?')) {
      return;
    }

    setRestoreBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_BASE_URL}/core/restore/`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Wiederherstellung abgeschlossen.');
    } catch (error) {
      console.error('Fehler beim Wiederherstellen:', error);
      const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Wiederherstellen';
      alert(errorMsg);
    }
    setRestoreBusy(false);
  }

  function startNewCalendar() {
    if (calendars.length >= 3) {
      alert('Maximallimit von 3 Kalendern erreicht');
      return;
    }
    setNewCalendar({
      name: '',
      ical_url: '',
      color: '#3b82f6',
      event_font_size: 6,
      is_active: true,
      use_start_date_only: false,
      all_events_as_holiday: false,
    });
  }

  function startNewTagOption() {
    setNewTagOption({
      name: '',
      kurz: '',
      farbe: '#22c55e',
      berechnungstyp: 'FEHLZEIT',
      aktiv: true,
      arbeitszeit_automatisch_berechnen: true,
      aktiv_mo: true,
      aktiv_di: true,
      aktiv_mi: true,
      aktiv_do: true,
      aktiv_fr: true,
      aktiv_sa: true,
      aktiv_so: true,
      beginn_mo: '07:00',
      ende_mo: '16:00',
      pause_mo: '00:30',
      beginn_di: '07:00',
      ende_di: '16:00',
      pause_di: '00:30',
      beginn_mi: '07:00',
      ende_mi: '16:00',
      pause_mi: '00:30',
      beginn_do: '07:00',
      ende_do: '16:00',
      pause_do: '00:30',
      beginn_fr: '07:00',
      ende_fr: '13:00',
      pause_fr: '00:30',
      beginn_sa: '',
      ende_sa: '',
      pause_sa: '',
      beginn_so: '',
      ende_so: '',
      pause_so: '',
    });
  }

  function startNewArbeitszeit() {
    setNewArbeitszeit({
      von_tag: 1,
      von_monat: 1,
      bis_tag: 31,
      bis_monat: 12,
      max_mo_do: 8,
      max_fr: 6,
      beginn_mo_do: '07:00',
      ende_mo_do: '16:00',
      pause_mo_do: '00:30',
      beginn_fr: '07:00',
      ende_fr: '13:00',
      pause_fr: '00:30'
    });
  }

  function startNewStundenverrechnungGlobal() {
    setNewStundenverrechnungGlobal({
      wert: '',
      gueltig_ab: new Date().toISOString().slice(0, 10),
    });
  }

  function startNewRechnungsaufschlagGlobal() {
    setNewRechnungsaufschlagGlobal({
      wert: '',
      gueltig_ab: new Date().toISOString().slice(0, 10),
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
          Einstellungen
        </h2>
      </div>

      <div className="card" style={{ marginBottom: '2rem', padding: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {SETTINGS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`btn ${activeTab === tab.id ? 'btn-primary' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: activeTab === 'stundenkalender' ? 'block' : 'none' }}>
      {/* Kalender-Verwaltung */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={24} />
              Externe Kalender (iCal)
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
              Bis zu 3 externe Kalender können importiert werden
            </p>
          </div>
          {calendars.length < 3 && !newCalendar && (
            <button onClick={startNewCalendar} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Plus size={18} />
              Kalender hinzufügen
            </button>
          )}
        </div>

        {loading && calendars.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            Lade Kalender...
          </div>
        ) : (
          <>
            {/* New Calendar Form */}
            {newCalendar && (
              <CalendarForm
                calendar={newCalendar}
                onChange={setNewCalendar}
                onSave={() => saveCalendar(newCalendar)}
                onCancel={() => setNewCalendar(null)}
              />
            )}

            {/* Existing Calendars */}
            {calendars.map(cal => (
              editingCalendar?.id === cal.id ? (
                <CalendarForm
                  key={cal.id}
                  calendar={editingCalendar}
                  onChange={setEditingCalendar}
                  onSave={() => saveCalendar(editingCalendar)}
                  onCancel={() => setEditingCalendar(null)}
                />
              ) : (
                <CalendarCard
                  key={cal.id}
                  calendar={cal}
                  onEdit={() => setEditingCalendar({ ...cal })}
                  onDelete={() => deleteCalendar(cal.id)}
                  onSync={() => syncCalendar(cal.id)}
                  syncing={loading}
                />
              )
            ))}

            {calendars.length === 0 && !newCalendar && (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                <AlertCircle size={48} style={{ opacity: 0.5, margin: '0 auto 1rem' }} />
                <p>Noch keine Kalender konfiguriert</p>
                <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Fügen Sie externe iCal-Kalender hinzu um Events zu importieren
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Info Box */}
      <div className="card" style={{ background: '#eff6ff', borderLeft: '4px solid #3b82f6' }}>
        <h4 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>ℹ️ iCal URL finden</h4>
        <ul style={{ fontSize: '0.875rem', color: '#1e40af', paddingLeft: '1.5rem', margin: 0 }}>
          <li>Google Calendar: Einstellungen → Kalender → iCal-Adresse kopieren</li>
          <li>Outlook: Kalender freigeben → iCal-Link kopieren</li>
          <li>Apple Calendar: Kalender → Veröffentlichen → Webadresse kopieren</li>
        </ul>
      </div>

      <div className="card" style={{ marginTop: '2rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
          Kalender-Ansicht
        </h3>
        <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
          Normale Anzeige (Wochen)
        </label>
        <input
          type="number"
          min="1"
          className="input"
          value={calendarDefaultWeeks}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            setCalendarDefaultWeeks(Number.isNaN(value) ? 1 : Math.max(1, value));
          }}
          style={{ maxWidth: '200px' }}
        />
        <label style={{ display: 'block', fontWeight: '500', marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
          Baustellen-Statistik: Normale Anzeige (Wochen)
        </label>
        <input
          type="number"
          min="1"
          className="input"
          value={baustellenStatsDefaultWeeks}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            setBaustellenStatsDefaultWeeks(Number.isNaN(value) ? 1 : Math.max(1, value));
          }}
          style={{ maxWidth: '200px' }}
        />
        <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={calendarShowEmployees}
              onChange={(e) => setCalendarShowEmployees(e.target.checked)}
              style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.875rem', color: '#374151' }}>Mitarbeiter beim Start anzeigen</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={calendarShowSubunternehmer}
              onChange={(e) => setCalendarShowSubunternehmer(e.target.checked)}
              style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.875rem', color: '#374151' }}>Subunternehmer beim Start anzeigen</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={baustellenStatsShowPerson}
              onChange={(e) => setBaustellenStatsShowPerson(e.target.checked)}
              style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.875rem', color: '#374151' }}>
              Baustellen-Statistik: Personennamen anzeigen
            </span>
          </label>
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Urlaub Farbe
            </label>
            <input
              type="color"
              value={urlaubColor}
              onChange={(e) => setUrlaubColor(e.target.value)}
              className="input"
              style={{ width: '80px', height: '40px', padding: '0.25rem' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Krankheit Farbe
            </label>
            <input
              type="color"
              value={krankColor}
              onChange={(e) => setKrankColor(e.target.value)}
              className="input"
              style={{ width: '80px', height: '40px', padding: '0.25rem' }}
            />
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
            Tag-Optionen (Stundenerfassung)
          </h3>
          {!newTagOption && (
            <button onClick={startNewTagOption} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Plus size={18} />
              Option hinzufügen
            </button>
          )}
        </div>

        {newTagOption && (
          <TagOptionForm
            option={newTagOption}
            onChange={setNewTagOption}
            onSave={(payload) => saveTagOption(payload)}
            onCancel={() => setNewTagOption(null)}
          />
        )}

        {tagOptionen.map(opt => (
          editingTagOption?.id === opt.id ? (
            <TagOptionForm
              key={opt.id}
              option={editingTagOption}
              onChange={setEditingTagOption}
              onSave={(payload) => saveTagOption(payload)}
              onCancel={() => setEditingTagOption(null)}
            />
          ) : (
            <TagOptionCard
              key={opt.id}
              option={opt}
              onEdit={() => setEditingTagOption({ ...opt })}
              onDelete={() => deleteTagOption(opt.id)}
            />
          )
        ))}
        <div style={{ marginTop: '1rem', maxWidth: '360px' }}>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Textlänge Beschreibung Arbeitseintrag
          </label>
          <input
            type="number"
            min="1"
            step="1"
            value={appSettings.description_max_length ?? 40}
            onChange={(e) => setAppSettings({ ...appSettings, description_max_length: e.target.value })}
            className="input"
          />
        </div>
      </div>

      </div>

      <div style={{ display: activeTab === 'branding' ? 'block' : 'none' }}>
      <div className="card" style={{ marginTop: '2rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
          Branding
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '1rem', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Firmenname
            </label>
            <input
              type="text"
              value={appSettings.company_name || ''}
              onChange={(e) => setAppSettings({ ...appSettings, company_name: e.target.value })}
              className="input"
              placeholder="Firmenname"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Primärfarbe
            </label>
            <input
              type="color"
              value={appSettings.primary_color || '#0ea5e9'}
              onChange={(e) => setAppSettings({ ...appSettings, primary_color: e.target.value })}
              className="input"
              style={{ width: '80px', height: '40px', padding: '0.25rem' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Logo hochladen
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setAppSettingsFiles({ ...appSettingsFiles, logo: e.target.files?.[0] || null })}
              className="input"
            />
            {appSettings.logo && (
              <div style={{ marginTop: '0.5rem' }}>
                <img src={appSettings.logo} alt="Logo" style={{ height: '32px', width: 'auto' }} />
              </div>
            )}
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Favicon hochladen
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setAppSettingsFiles({ ...appSettingsFiles, favicon: e.target.files?.[0] || null })}
              className="input"
            />
            {appSettings.favicon && (
              <div style={{ marginTop: '0.5rem' }}>
                <img src={appSettings.favicon} alt="Favicon" style={{ height: '24px', width: '24px' }} />
              </div>
            )}
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              ODS Vorlage hochladen
            </label>
            <input
              type="file"
              accept=".ods,application/vnd.oasis.opendocument.spreadsheet"
              onChange={(e) => setAppSettingsFiles({ ...appSettingsFiles, ods_template: e.target.files?.[0] || null })}
              className="input"
            />
            {appSettings.ods_template && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                Vorlage: {appSettings.ods_template.split('/').pop()}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button onClick={saveBrandingTab} className="btn btn-primary">
            Speichern
          </button>
        </div>
      </div>

      </div>
      <div style={{ display: activeTab === 'nextcloud' ? 'block' : 'none' }}>
      <div className="card" style={{ marginTop: '2rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
          Nextcloud WebDAV
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr', gap: '1rem', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              WebDAV URL
            </label>
            <input
              type="url"
              value={nextcloudSettings.webdav_url || ''}
              onChange={(e) => setNextcloudSettings({ ...nextcloudSettings, webdav_url: e.target.value })}
              className="input"
              placeholder="https://cloud.example.de/remote.php/dav/files/USER/"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Benutzername
            </label>
            <input
              type="text"
              value={nextcloudSettings.username || ''}
              onChange={(e) => setNextcloudSettings({ ...nextcloudSettings, username: e.target.value })}
              className="input"
              placeholder="Benutzername"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Passwort / App-Passwort
            </label>
            <input
              type="password"
              value={nextcloudSettings.password || ''}
              onChange={(e) => setNextcloudSettings({ ...nextcloudSettings, password: e.target.value })}
              className="input"
              placeholder="Passwort"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Basis-Pfad (Root im Programm)
            </label>
            <input
              type="text"
              value={nextcloudSettings.base_path || ''}
              onChange={(e) => setNextcloudSettings({ ...nextcloudSettings, base_path: e.target.value })}
              className="input"
              placeholder="z.B. Baustellen/2026"
            />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: '1rem' }}>
          <input
            type="checkbox"
            checked={!!nextcloudSettings.aktiv}
            onChange={(e) => setNextcloudSettings({ ...nextcloudSettings, aktiv: e.target.checked })}
            style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
          />
          <span style={{ fontSize: '0.875rem', color: '#374151' }}>Nextcloud-Anbindung aktiv</span>
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button onClick={saveNextcloudTab} className="btn btn-primary">
            Speichern
          </button>
        </div>
      </div>

      </div>
      <div style={{ display: activeTab === 'rechnungen' ? 'block' : 'none' }}>
      <div className="card" style={{ marginTop: '2rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
          Rechnungen
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr auto', gap: '0.75rem', alignItems: 'end', maxWidth: '900px' }}>
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Ordner für Eingangsrechnungen in Nextcloud
            </label>
            <input
              type="text"
              value={nextcloudSettings.rechnungen_path || ''}
              onChange={(e) => setNextcloudSettings({ ...nextcloudSettings, rechnungen_path: e.target.value })}
              className="input"
              placeholder="z.B. Buchhaltung/Eingangsrechnungen"
            />
          </div>
          <button onClick={openRechnungenPathPicker} className="btn" type="button">
            Ordner auswählen
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button onClick={saveNextcloudSettings} className="btn btn-primary">
            Speichern
          </button>
        </div>

        <div style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
          <h4 style={{ margin: 0, marginBottom: '0.75rem', fontSize: '1rem', fontWeight: 600 }}>
            Analyse-Modus
          </h4>
          <div style={{ display: 'grid', gap: '0.75rem', maxWidth: '900px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Standard-Analyzer
              </label>
              <select
                className="input"
                value={rechnungsanalyseSettings.analyzer || 'invoice2data'}
                onChange={(e) => setRechnungsanalyseSettings({ ...rechnungsanalyseSettings, analyzer: e.target.value })}
              >
                <option value="invoice2data">invoice2data (Template-basiert)</option>
                <option value="llm">LLM (KI-Analyse)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Dateiname-Muster für Rechnungszuordnung
              </label>
              <input
                className="input"
                type="text"
                value={rechnungsanalyseSettings.invoice_filename_pattern || ''}
                onChange={(e) => setRechnungsanalyseSettings({ ...rechnungsanalyseSettings, invoice_filename_pattern: e.target.value })}
                placeholder="{{date}}_{{baustelle}}_{{issuer}}_{{invoice_number}}_{{amount}}"
              />
              <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '0.35rem' }}>
                Verfügbare Platzhalter: {'{{date}}'}, {'{{baustelle}}'}, {'{{issuer}}'}, {'{{invoice_number}}'}, {'{{amount}}'}, {'{{currency}}'}, {'{{original_name}}'}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                  LLM API URL
                </label>
                <input
                  className="input"
                  type="url"
                  value={rechnungsanalyseSettings.llm_api_url || ''}
                  onChange={(e) => setRechnungsanalyseSettings({ ...rechnungsanalyseSettings, llm_api_url: e.target.value })}
                  placeholder="https://api.openai.com/v1/chat/completions"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                  LLM Modell
                </label>
                <input
                  className="input"
                  type="text"
                  value={rechnungsanalyseSettings.llm_model || ''}
                  onChange={(e) => setRechnungsanalyseSettings({ ...rechnungsanalyseSettings, llm_model: e.target.value })}
                  placeholder="gpt-4o-mini"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                  LLM API Key
                </label>
                <input
                  className="input"
                  type="password"
                  value={rechnungsanalyseSettings.llm_api_key || ''}
                  onChange={(e) => setRechnungsanalyseSettings({ ...rechnungsanalyseSettings, llm_api_key: e.target.value })}
                  placeholder="sk-..."
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                LLM System-Prompt
              </label>
              <textarea
                className="input"
                rows={4}
                value={rechnungsanalyseSettings.llm_prompt || ''}
                onChange={(e) => setRechnungsanalyseSettings({ ...rechnungsanalyseSettings, llm_prompt: e.target.value })}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                LLM Prompt für Positions-Erkennung
              </label>
              <textarea
                className="input"
                rows={4}
                value={rechnungsanalyseSettings.llm_items_prompt || ''}
                onChange={(e) => setRechnungsanalyseSettings({ ...rechnungsanalyseSettings, llm_items_prompt: e.target.value })}
                placeholder='JSON-Format: {"items":[{"article_no":"","description":"","quantity":0,"unit":"","unit_price":0,"line_total":0}]}'
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                LLM Prompt für Template-Erstellung Stufe 1 (Struktur-Analyse)
              </label>
              <textarea
                className="input"
                rows={8}
                value={rechnungsanalyseSettings.llm_template_analysis_prompt || ''}
                onChange={(e) => setRechnungsanalyseSettings({ ...rechnungsanalyseSettings, llm_template_analysis_prompt: e.target.value })}
                placeholder='Stufe 1: JSON-Strukturanalyse aus Rechnungstext'
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                LLM Prompt für Template-Erstellung Stufe 2 (YAML-Generierung)
              </label>
              <textarea
                className="input"
                rows={8}
                value={rechnungsanalyseSettings.llm_template_generation_prompt || ''}
                onChange={(e) => setRechnungsanalyseSettings({ ...rechnungsanalyseSettings, llm_template_generation_prompt: e.target.value })}
                placeholder='Stufe 2: JSON mit template_name, issuer, yaml_content, design_notes'
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
            <button onClick={saveRechnungsanalyseSettings} className="btn btn-primary">
              Analyse-Einstellungen speichern
            </button>
          </div>
        </div>

        <div style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Lieferantenspezifische Regeln</h4>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Regeln für Lieferantenerkennung, Template-Priorisierung, Typ-/Match-Hinweise und Qualitätsgrenzen.
              </div>
            </div>
            {!newLieferantenRegel && (
              <button onClick={startNewLieferantenRegel} className="btn btn-primary" type="button">
                <Plus size={16} /> Regel hinzufügen
              </button>
            )}
          </div>

          {lieferantenRegelnLoading && (
            <div className="card" style={{ marginBottom: '0.75rem', background: '#f9fafb' }}>
              Lieferantenregeln werden geladen...
            </div>
          )}

          {lieferantenRegelnError && (
            <div className="card" style={{ marginBottom: '0.75rem', borderLeft: '4px solid #dc2626', background: '#fef2f2' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#991b1b' }}>
                <AlertCircle size={16} />
                <span>{lieferantenRegelnError}</span>
              </div>
            </div>
          )}

          {newLieferantenRegel && (
            <LieferantenRegelForm
              title="Neue Lieferantenregel"
              item={newLieferantenRegel}
              templates={rechnungTemplates}
              onChange={setNewLieferantenRegel}
              onSave={() => saveLieferantenRegel(newLieferantenRegel)}
              onCancel={() => setNewLieferantenRegel(null)}
              isNew
            />
          )}

          {!lieferantenRegelnLoading && !lieferantenRegeln.length && !newLieferantenRegel && !lieferantenRegelnError && (
            <div className="card" style={{ marginBottom: '0.75rem', background: '#f9fafb' }}>
              Keine Lieferantenregeln vorhanden.
            </div>
          )}

          {lieferantenRegeln.map((entry) => (
            <LieferantenRegelForm
              key={entry.id}
              item={entry}
              templates={rechnungTemplates}
              onChange={(next) => updateLieferantenRegelLocal(entry.id, next)}
              onSave={() => saveLieferantenRegel(entry)}
            />
          ))}
        </div>

        <div style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Rechnungspositions-Typen</h4>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Pflege von Typen, Keywords und typischen Einheiten für die automatische Rechnungsanalyse.
              </div>
            </div>
            {!newRechnungspositionsTyp && (
              <button onClick={startNewRechnungspositionsTyp} className="btn btn-primary" type="button">
                <Plus size={16} /> Typ hinzufügen
              </button>
            )}
          </div>

          {rechnungspositionsTypenLoading && (
            <div className="card" style={{ marginBottom: '0.75rem', background: '#f9fafb' }}>
              Rechnungspositions-Typen werden geladen...
            </div>
          )}

          {rechnungspositionsTypenError && (
            <div className="card" style={{ marginBottom: '0.75rem', borderLeft: '4px solid #dc2626', background: '#fef2f2' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#991b1b' }}>
                <AlertCircle size={16} />
                <span>{rechnungspositionsTypenError}</span>
              </div>
            </div>
          )}

          {newRechnungspositionsTyp && (
            <RechnungspositionsTypForm
              title="Neuen Typ anlegen"
              item={newRechnungspositionsTyp}
              onChange={setNewRechnungspositionsTyp}
              onSave={() => saveRechnungspositionsTyp(newRechnungspositionsTyp)}
              onCancel={() => setNewRechnungspositionsTyp(null)}
              isNew
            />
          )}

          {!rechnungspositionsTypenLoading && !rechnungspositionsTypen.length && !newRechnungspositionsTyp && !rechnungspositionsTypenError && (
            <div className="card" style={{ marginBottom: '0.75rem', background: '#f9fafb' }}>
              Keine Rechnungspositions-Typen vorhanden.
            </div>
          )}

          {rechnungspositionsTypen.map((entry) => (
            <RechnungspositionsTypForm
              key={entry.id}
              item={entry}
              onChange={(next) => updateRechnungspositionsTypLocal(entry.id, next)}
              onSave={() => saveRechnungspositionsTyp(entry)}
            />
          ))}
        </div>

        <div style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>invoice2data Templates</h4>
            {!newTemplate && (
              <button onClick={startNewTemplate} className="btn btn-primary">
                <Plus size={16} /> Template hinzufügen
              </button>
            )}
          </div>

          {newTemplate && (
            <TemplateForm
              template={newTemplate}
              onChange={setNewTemplate}
              onSave={() => saveTemplate(newTemplate)}
              onCancel={() => setNewTemplate(null)}
            />
          )}

          {rechnungTemplates.map((tpl) => (
            editingTemplate?.id === tpl.id ? (
              <TemplateForm
                key={tpl.id}
                template={editingTemplate}
                onChange={setEditingTemplate}
                onSave={() => saveTemplate(editingTemplate)}
                onCancel={() => setEditingTemplate(null)}
              />
            ) : (
              <div key={tpl.id} className="card" style={{ marginBottom: '0.75rem', background: '#f9fafb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{tpl.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                      Issuer: {tpl.issuer || '-'} • {tpl.aktiv ? 'Aktiv' : 'Inaktiv'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn" onClick={() => setEditingTemplate({ ...tpl })}>Bearbeiten</button>
                    <button className="btn" style={{ color: '#dc2626' }} onClick={() => deleteTemplate(tpl.id)}>
                      <Trash2 size={14} /> Löschen
                    </button>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
        <button onClick={saveRechnungenTab} className="btn btn-primary">
          Speichern
        </button>
      </div>
      </div>

      <div style={{ display: activeTab === 'stundenkalender' ? 'block' : 'none' }}>
      <div className="card" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
            Arbeitszeit-Zeitraeume
          </h3>
          {!newArbeitszeit && (
            <button onClick={startNewArbeitszeit} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Plus size={18} />
              Zeitraum hinzufügen
            </button>
          )}
        </div>

        {newArbeitszeit && (
          <ArbeitszeitForm
            zeitraum={newArbeitszeit}
            onChange={setNewArbeitszeit}
            onSave={() => saveArbeitszeit(newArbeitszeit)}
            onCancel={() => setNewArbeitszeit(null)}
          />
        )}

        {arbeitszeitraeume.map(z => (
          editingArbeitszeit?.id === z.id ? (
            <ArbeitszeitForm
              key={z.id}
              zeitraum={editingArbeitszeit}
              onChange={setEditingArbeitszeit}
              onSave={() => saveArbeitszeit(editingArbeitszeit)}
              onCancel={() => setEditingArbeitszeit(null)}
            />
          ) : (
            <ArbeitszeitCard
              key={z.id}
              zeitraum={z}
              onEdit={() => setEditingArbeitszeit({ ...z })}
              onDelete={() => deleteArbeitszeit(z.id)}
            />
          )
        ))}
      </div>

      <div className="card" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
            Stundenverrechnungspauschalen global
          </h3>
          {!newStundenverrechnungGlobal && (
            <button onClick={startNewStundenverrechnungGlobal} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Plus size={18} />
              Eintrag hinzufügen
            </button>
          )}
        </div>

        {newStundenverrechnungGlobal && (
          <StundenverrechnungForm
            eintrag={newStundenverrechnungGlobal}
            onChange={setNewStundenverrechnungGlobal}
            onSave={() => saveStundenverrechnungGlobal(newStundenverrechnungGlobal)}
            onCancel={() => setNewStundenverrechnungGlobal(null)}
          />
        )}

        {stundenverrechnungGlobal.map((eintrag) => (
          editingStundenverrechnungGlobal?.id === eintrag.id ? (
            <StundenverrechnungForm
              key={eintrag.id}
              eintrag={editingStundenverrechnungGlobal}
              onChange={setEditingStundenverrechnungGlobal}
              onSave={() => saveStundenverrechnungGlobal(editingStundenverrechnungGlobal)}
              onCancel={() => setEditingStundenverrechnungGlobal(null)}
            />
          ) : (
            <StundenverrechnungCard
              key={eintrag.id}
              eintrag={eintrag}
              onEdit={() => setEditingStundenverrechnungGlobal({ ...eintrag })}
              onDelete={() => deleteStundenverrechnungGlobal(eintrag.id)}
            />
          )
        ))}
      </div>

      <div className="card" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
            Rechnungs-Aufschlag global
          </h3>
          {!newRechnungsaufschlagGlobal && (
            <button onClick={startNewRechnungsaufschlagGlobal} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Plus size={18} />
              Eintrag hinzufügen
            </button>
          )}
        </div>

        {newRechnungsaufschlagGlobal && (
          <RechnungsaufschlagForm
            eintrag={newRechnungsaufschlagGlobal}
            onChange={setNewRechnungsaufschlagGlobal}
            onSave={() => saveRechnungsaufschlagGlobal(newRechnungsaufschlagGlobal)}
            onCancel={() => setNewRechnungsaufschlagGlobal(null)}
          />
        )}

        {rechnungsaufschlagGlobal.map((eintrag) => (
          editingRechnungsaufschlagGlobal?.id === eintrag.id ? (
            <RechnungsaufschlagForm
              key={eintrag.id}
              eintrag={editingRechnungsaufschlagGlobal}
              onChange={setEditingRechnungsaufschlagGlobal}
              onSave={() => saveRechnungsaufschlagGlobal(editingRechnungsaufschlagGlobal)}
              onCancel={() => setEditingRechnungsaufschlagGlobal(null)}
            />
          ) : (
            <RechnungsaufschlagCard
              key={eintrag.id}
              eintrag={eintrag}
              onEdit={() => setEditingRechnungsaufschlagGlobal({ ...eintrag })}
              onDelete={() => deleteRechnungsaufschlagGlobal(eintrag.id)}
            />
          )
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
        <button onClick={saveStundenkalenderTab} className="btn btn-primary">
          Speichern
        </button>
      </div>
      </div>

      <div style={{ display: activeTab === 'backup' ? 'block' : 'none' }}>
      <div className="card" style={{ marginTop: '2rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
          Backup und Wiederherstellung
        </h3>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
          Erstellt eine Sicherung der gesamten Datenbank oder spielt sie wieder ein.
        </p>
        <div style={{ display: 'grid', gap: '1rem', maxWidth: '900px', marginBottom: '1.25rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', fontWeight: 500 }}>
            <input
              type="checkbox"
              checked={!!appSettings.backup_to_nextcloud_enabled}
              onChange={(e) => setAppSettings({ ...appSettings, backup_to_nextcloud_enabled: e.target.checked })}
            />
            Automatisches Backup in Nextcloud erstellen
          </label>

          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.45rem', fontSize: '0.875rem' }}>
              Verzeichnis auf Nextcloud
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem', alignItems: 'center' }}>
              <div
                className="input"
                style={{
                  minHeight: '42px',
                  display: 'flex',
                  alignItems: 'center',
                  color: appSettings.backup_nextcloud_path ? '#111827' : '#6b7280'
                }}
              >
                {appSettings.backup_nextcloud_path ? `/${appSettings.backup_nextcloud_path}` : 'Kein Ordner ausgewählt'}
              </div>
              <button onClick={openBackupPathPicker} className="btn" type="button">
                Ordner auswählen
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.45rem', fontSize: '0.875rem' }}>
                Sicherungsintervall
              </label>
              <select
                className="input"
                value={appSettings.backup_interval || 'daily'}
                onChange={(e) => setAppSettings({ ...appSettings, backup_interval: e.target.value })}
              >
                <option value="daily">Täglich</option>
                <option value="weekly">Wöchentlich</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.45rem', fontSize: '0.875rem' }}>
                Uhrzeit
              </label>
              <select
                className="input"
                value={String(appSettings.backup_hour ?? 2)}
                onChange={(e) => setAppSettings({ ...appSettings, backup_hour: parseInt(e.target.value, 10) })}
              >
                {Array.from({ length: 24 }, (_, hour) => (
                  <option key={hour} value={hour}>
                    {String(hour).padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.45rem', fontSize: '0.875rem' }}>
                X Kopien aufheben
              </label>
              <input
                type="number"
                min="1"
                step="1"
                className="input"
                value={appSettings.backup_retention_copies ?? 10}
                onChange={(e) => setAppSettings({ ...appSettings, backup_retention_copies: e.target.value })}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={testNextcloudBackup} className="btn" disabled={backupTestBusy}>
                {backupTestBusy ? 'Test läuft...' : 'Testen'}
              </button>
              <button onClick={saveBackupTab} className="btn btn-primary">
              Backup-Einstellungen speichern
              </button>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            onClick={downloadBackup}
            disabled={backupBusy}
          >
            Backup herunterladen
          </button>
          <button
            className="btn"
            onClick={triggerRestore}
            disabled={restoreBusy}
          >
            Backup wiederherstellen
          </button>
          <input
            ref={restoreInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleRestoreFile}
            style={{ display: 'none' }}
          />
        </div>
      </div>
      </div>

      {showNextcloudPicker && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 2500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div className="card" style={{ width: '100%', maxWidth: '760px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                {nextcloudPickerTarget === 'backup' ? 'Backup-Ordner in Nextcloud wählen' : 'Nextcloud-Verzeichnis wählen'}
              </h3>
              <button onClick={closeNextcloudPicker} className="btn">
                <X size={16} /> Schließen
              </button>
            </div>

            <div style={{ marginBottom: '0.75rem', fontSize: '0.9rem' }}>
              <strong>Aktueller Pfad:</strong> {nextcloudPickerCurrentPath ? `/${nextcloudPickerCurrentPath}` : '/'}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button className="btn" onClick={() => loadNextcloudDirectories('')} disabled={nextcloudPickerLoading}>
                Root
              </button>
              <button className="btn" onClick={goToNextcloudParent} disabled={nextcloudPickerLoading || !nextcloudPickerCurrentPath}>
                Eine Ebene hoch
              </button>
            </div>

            {nextcloudPickerError && (
              <div style={{ color: '#b91c1c', background: '#fee2e2', borderRadius: '0.5rem', padding: '0.5rem', marginBottom: '0.75rem' }}>
                {nextcloudPickerError}
              </div>
            )}

            <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', maxHeight: '45vh', overflowY: 'auto', padding: '0.5rem' }}>
              {nextcloudPickerLoading ? (
                <div style={{ color: '#6b7280' }}>Lade Verzeichnisse...</div>
              ) : nextcloudPickerDirs.length === 0 ? (
                <div style={{ color: '#6b7280' }}>Keine Unterverzeichnisse vorhanden.</div>
              ) : (
                nextcloudPickerDirs.map((dir) => (
                  <button
                    key={dir.path || dir.name}
                    className="btn"
                    onClick={() => loadNextcloudDirectories(dir.path)}
                    style={{ width: '100%', marginBottom: '0.35rem', justifyContent: 'flex-start' }}
                    title={`/${dir.path}`}
                  >
                    {dir.name}
                  </button>
                ))
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-primary" onClick={confirmNextcloudPathSelection}>
                Diesen Ordner verwenden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateForm({ template, onChange, onSave, onCancel }) {
  return (
    <div className="card" style={{ marginBottom: '0.75rem', background: '#f9fafb' }}>
      <div style={{ display: 'grid', gap: '0.75rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Name</label>
            <input
              className="input"
              type="text"
              value={template.name || ''}
              onChange={(e) => onChange({ ...template, name: e.target.value })}
              placeholder="z.B. Lieferant_XYZ"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Issuer (optional)</label>
            <input
              className="input"
              type="text"
              value={template.issuer || ''}
              onChange={(e) => onChange({ ...template, issuer: e.target.value })}
              placeholder="z.B. Muster GmbH"
            />
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>YAML Template-Inhalt</label>
          <textarea
            className="input"
            rows={10}
            value={template.yaml_content || ''}
            onChange={(e) => onChange({ ...template, yaml_content: e.target.value })}
            placeholder={'issuer: Muster GmbH\nkeywords:\n  - "Muster GmbH"\nfields:\n  invoice_number: "Rechnungsnummer[:\\\\s]+([A-Z0-9-]+)"'}
          />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={template.aktiv !== false}
            onChange={(e) => onChange({ ...template, aktiv: e.target.checked })}
            style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
          />
          <span style={{ fontSize: '0.875rem', color: '#374151' }}>Template aktiv</span>
        </label>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="btn"><X size={16} /> Abbrechen</button>
          <button onClick={onSave} className="btn btn-primary"><Check size={16} /> Speichern</button>
        </div>
      </div>
    </div>
  );
}

function CalendarForm({ calendar, onChange, onSave, onCancel }) {
  return (
    <div className="card" style={{ marginBottom: '1rem', background: '#f9fafb' }}>
      <div style={{ display: 'grid', gap: '1rem' }}>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Name des Kalenders
          </label>
          <input
            type="text"
            value={calendar.name}
            onChange={(e) => onChange({ ...calendar, name: e.target.value })}
            className="input"
            placeholder="z.B. Feiertage, Urlaub, ..."
          />
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            iCal URL (.ics)
          </label>
          <input
            type="url"
            value={calendar.ical_url}
            onChange={(e) => onChange({ ...calendar, ical_url: e.target.value })}
            className="input"
            placeholder="https://..."
          />
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Farbe
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {DEFAULT_COLORS.map(c => (
              <button
                key={c.value}
                onClick={() => onChange({ ...calendar, color: c.value })}
                style={{
                  width: '3rem',
                  height: '3rem',
                  background: c.value,
                  border: calendar.color === c.value ? '3px solid #000' : '2px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={c.label}
              >
                {calendar.color === c.value && <Check size={20} color="white" />}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Schriftgröße der Einträge (px)
          </label>
          <input
            type="number"
            min="4"
            max="20"
            value={calendar.event_font_size ?? 6}
            onChange={(e) => onChange({ ...calendar, event_font_size: parseInt(e.target.value, 10) || 6 })}
            className="input"
            style={{ maxWidth: '200px' }}
          />
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={calendar.use_start_date_only || false}
              onChange={(e) => onChange({ ...calendar, use_start_date_only: e.target.checked })}
              style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
            />
            <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
              Nur Startdatum verwenden
            </span>
          </label>
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', marginLeft: '1.75rem' }}>
            Events werden nur am Startdatum angezeigt, nicht über den gesamten Zeitraum
          </p>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={calendar.all_events_as_holiday || false}
              onChange={(e) => onChange({ ...calendar, all_events_as_holiday: e.target.checked })}
              style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
            />
            <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
              Alle Ereignisse als Feiertag eintragen
            </span>
          </label>
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', marginLeft: '1.75rem' }}>
            Tage mit mindestens einem Ereignis dieses Kalenders werden in der Stundenerfassung wie Feiertage behandelt.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="btn">
            <X size={18} /> Abbrechen
          </button>
          <button
            onClick={onSave}
            className="btn btn-primary"
            disabled={!calendar.name || !calendar.ical_url}
          >
            <Check size={18} /> Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function CalendarCard({ calendar, onEdit, onDelete, onSync, syncing }) {
  return (
    <div className="card" style={{ marginBottom: '1rem', borderLeft: `4px solid ${calendar.color}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{
              width: '1rem',
              height: '1rem',
              background: calendar.color,
              borderRadius: '0.25rem'
            }} />
            <h4 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
              {calendar.name}
            </h4>
            {!calendar.is_active && (
              <span style={{
                fontSize: '0.75rem',
                padding: '0.25rem 0.5rem',
                background: '#fee2e2',
                color: '#dc2626',
                borderRadius: '0.25rem'
              }}>
                Inaktiv
              </span>
            )}
          </div>

          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem', wordBreak: 'break-all' }}>
            {calendar.ical_url}
          </p>

          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
            {calendar.last_synced ? (
              <>
                Zuletzt synchronisiert: {new Date(calendar.last_synced).toLocaleString('de-DE')}
                {' • '}
                {calendar.events_count || 0} Events
              </>
            ) : (
              'Noch nie synchronisiert'
            )}
          </div>
          {calendar.all_events_as_holiday && (
            <div style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '0.35rem' }}>
              Alle Ereignisse werden als Feiertag behandelt
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={onSync}
            disabled={syncing}
            className="btn"
            title="Synchronisieren"
          >
            <RefreshCw size={18} className={syncing ? 'spinning' : ''} />
          </button>
          <button onClick={onEdit} className="btn" title="Bearbeiten">
            Bearbeiten
          </button>
          <button
            onClick={onDelete}
            style={{ color: '#dc2626' }}
            className="btn"
            title="Löschen"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

function RechnungspositionsTypForm({ item, onChange, onSave, onCancel, title, isNew = false }) {
  const saveStateColor = item._saveState === 'error'
    ? '#dc2626'
    : item._saveState === 'saved'
      ? '#059669'
      : item._saveState === 'saving'
        ? '#2563eb'
        : '#6b7280';

  const saveStateLabel = item._saveState === 'error'
    ? 'Fehler'
    : item._saveState === 'saved'
      ? 'Gespeichert'
      : item._saveState === 'saving'
        ? 'Speichert...'
        : item._saveState === 'unsaved'
          ? 'Ungespeichert'
          : 'Bereit';

  function update(patch) {
    onChange({
      ...item,
      ...patch,
      _saveState: 'unsaved',
      _saveError: '',
    });
  }

  return (
    <div
      className="card"
      style={{
        marginBottom: '1rem',
        borderLeft: `4px solid ${item.farbe || (item.aktiv ? '#2563eb' : '#9ca3af')}`,
        opacity: item.aktiv === false ? 0.75 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '1rem' }}>
            {title || item.label || item.key || 'Rechnungspositions-Typ'}
          </div>
          {!isNew && (
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.2rem' }}>
              Interner Schlüssel: {item.key || '-'}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: saveStateColor }}>
            {saveStateLabel}
          </div>
          {item._saveError ? (
            <div style={{ fontSize: '0.78rem', color: '#b91c1c', marginTop: '0.2rem', maxWidth: '260px' }}>
              {item._saveError}
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Label
          </label>
          <input
            className="input"
            type="text"
            value={item.label || ''}
            onChange={(e) => update({ label: e.target.value })}
            placeholder="z.B. Material"
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Key
          </label>
          <input
            className="input"
            type="text"
            value={item.key || ''}
            onChange={(e) => update({ key: e.target.value })}
            placeholder="z.B. material"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Beschreibung
          </label>
          <input
            className="input"
            type="text"
            value={item.beschreibung || ''}
            onChange={(e) => update({ beschreibung: e.target.value })}
            placeholder="Kurze Beschreibung des Typs"
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Farbe
          </label>
          <input
            className="input"
            type="text"
            value={item.farbe || ''}
            onChange={(e) => update({ farbe: e.target.value })}
            placeholder="#2563eb"
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Sortierung
          </label>
          <input
            className="input"
            type="number"
            value={item.sortierung ?? 100}
            onChange={(e) => update({ sortierung: e.target.value })}
            placeholder="100"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Keywords
          </label>
          <textarea
            className="input"
            rows={5}
            value={item.keywords_text || ''}
            onChange={(e) => update({ keywords_text: e.target.value })}
            placeholder={'Begriffe zeilenweise oder komma-separiert\nz.B. bagger\nkran\nmiete'}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Einheiten
          </label>
          <textarea
            className="input"
            rows={5}
            value={item.einheiten_text || ''}
            onChange={(e) => update({ einheiten_text: e.target.value })}
            placeholder={'Einheiten zeilenweise oder komma-separiert\nz.B. h\nstd\nstunden'}
          />
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '1rem' }}>
        <input
          type="checkbox"
          checked={item.aktiv ?? true}
          onChange={(e) => update({ aktiv: e.target.checked })}
          style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
        />
        <span style={{ fontSize: '0.875rem', color: '#374151' }}>Aktiv</span>
      </label>

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        {onCancel ? (
          <button onClick={onCancel} className="btn" type="button">
            <X size={18} /> Abbrechen
          </button>
        ) : null}
        <button onClick={onSave} className="btn btn-primary" type="button" disabled={item._saveState === 'saving'}>
          <Check size={18} /> Speichern
        </button>
      </div>
    </div>
  );
}

function LieferantenRegelForm({ item, templates = [], onChange, onSave, onCancel, title, isNew = false }) {
  const saveStateColor = item._saveState === 'error'
    ? '#dc2626'
    : item._saveState === 'saved'
      ? '#059669'
      : item._saveState === 'saving'
        ? '#2563eb'
        : '#6b7280';

  const saveStateLabel = item._saveState === 'error'
    ? 'Fehler'
    : item._saveState === 'saved'
      ? 'Gespeichert'
      : item._saveState === 'saving'
        ? 'Speichert...'
        : item._saveState === 'unsaved'
          ? 'Ungespeichert'
          : 'Bereit';

  function update(patch) {
    onChange({
      ...item,
      ...patch,
      _saveState: 'unsaved',
      _saveError: '',
    });
  }

  function toggleTemplate(templateId) {
    const currentIds = Array.isArray(item.preferred_template_ids_json) ? item.preferred_template_ids_json.map(String) : [];
    const targetId = String(templateId);
    const nextIds = currentIds.includes(targetId)
      ? currentIds.filter((value) => value !== targetId)
      : [...currentIds, targetId];
    update({ preferred_template_ids_json: nextIds });
  }

  return (
    <div
      className="card"
      style={{
        marginBottom: '1rem',
        borderLeft: `4px solid ${item.aktiv ? '#2563eb' : '#9ca3af'}`,
        opacity: item.aktiv === false ? 0.75 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '1rem' }}>
            {title || item.issuer_name || 'Lieferantenregel'}
          </div>
          {!isNew && (
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.2rem' }}>
              Priorität: {item.prioritaet ?? 100}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: saveStateColor }}>
            {saveStateLabel}
          </div>
          {item._saveError ? (
            <div style={{ fontSize: '0.78rem', color: '#b91c1c', marginTop: '0.2rem', maxWidth: '260px' }}>
              {item._saveError}
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Lieferantenname / Issuer
          </label>
          <input
            className="input"
            type="text"
            value={item.issuer_name || ''}
            onChange={(e) => update({ issuer_name: e.target.value })}
            placeholder="z.B. Kalkwerke GmbH"
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Priorität
          </label>
          <input
            className="input"
            type="number"
            value={item.prioritaet ?? 100}
            onChange={(e) => update({ prioritaet: e.target.value })}
            placeholder="100"
          />
        </div>
      </div>

      <div style={{ marginBottom: '0.75rem' }}>
        <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
          Beschreibung
        </label>
        <textarea
          className="input"
          rows={2}
          value={item.beschreibung || ''}
          onChange={(e) => update({ beschreibung: e.target.value })}
          placeholder="Kurzbeschreibung der Regel und des Lieferantenkontexts"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Match-Keywords
          </label>
          <textarea
            className="input"
            rows={4}
            value={item.match_keywords_text || ''}
            onChange={(e) => update({ match_keywords_text: e.target.value })}
            placeholder={'Begriffe zeilenweise oder komma-separiert\nz.B. kalkwerke\nasphalt\nbagger'}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Exclude-Keywords
          </label>
          <textarea
            className="input"
            rows={4}
            value={item.exclude_keywords_text || ''}
            onChange={(e) => update({ exclude_keywords_text: e.target.value })}
            placeholder={'Begriffe, die die Regel ausschließen\nz.B. gutschrift\nmahnung'}
          />
        </div>
      </div>

      <div style={{ marginBottom: '0.75rem' }}>
        <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
          Bevorzugte Templates
        </label>
        {templates.length === 0 ? (
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Keine Templates vorhanden.</div>
        ) : (
          <div style={{ display: 'grid', gap: '0.35rem', maxHeight: '180px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.6rem' }}>
            {templates.map((template) => {
              const checked = (item.preferred_template_ids_json || []).map(String).includes(String(template.id));
              return (
                <label key={template.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleTemplate(template.id)}
                    style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.85rem', color: '#374151' }}>
                    {template.name} {template.issuer ? `(${template.issuer})` : ''}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Typ-Hinweise (JSON)
          </label>
          <textarea
            className="input"
            rows={8}
            value={item.type_hints_text || ''}
            onChange={(e) => update({ type_hints_text: e.target.value })}
            placeholder={'{\n  "material": ["beton", "zement"],\n  "geraetestunden": ["miete", "bagger"]\n}'}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Match-Hinweise (JSON)
          </label>
          <textarea
            className="input"
            rows={8}
            value={item.match_hints_text || ''}
            onChange={(e) => update({ match_hints_text: e.target.value })}
            placeholder={'{\n  "prefer_sources": ["artikelstamm"],\n  "keywords": {\n    "historie": ["transport"]\n  }\n}'}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Qualitätsgrenzen (JSON)
          </label>
          <textarea
            className="input"
            rows={8}
            value={item.review_thresholds_text || ''}
            onChange={(e) => update({ review_thresholds_text: e.target.value })}
            placeholder={'{\n  "type_confidence_min": 0.55,\n  "match_score_min": 0.55,\n  "uncertain_rate_max": 0.35\n}'}
          />
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '1rem' }}>
        <input
          type="checkbox"
          checked={item.aktiv ?? true}
          onChange={(e) => update({ aktiv: e.target.checked })}
          style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
        />
        <span style={{ fontSize: '0.875rem', color: '#374151' }}>Regel aktiv</span>
      </label>

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        {onCancel ? (
          <button onClick={onCancel} className="btn" type="button">
            <X size={18} /> Abbrechen
          </button>
        ) : null}
        <button onClick={onSave} className="btn btn-primary" type="button" disabled={item._saveState === 'saving'}>
          <Check size={18} /> Speichern
        </button>
      </div>
    </div>
  );
}

function TagOptionForm({ option, onChange, onSave, onCancel }) {
  const weekdayConfigs = [
    { key: 'mo', label: 'Mo', defaultStart: '07:00', defaultEnd: '16:00' },
    { key: 'di', label: 'Di', defaultStart: '07:00', defaultEnd: '16:00' },
    { key: 'mi', label: 'Mi', defaultStart: '07:00', defaultEnd: '16:00' },
    { key: 'do', label: 'Do', defaultStart: '07:00', defaultEnd: '16:00' },
    { key: 'fr', label: 'Fr', defaultStart: '07:00', defaultEnd: '13:00' },
    { key: 'sa', label: 'Sa', defaultStart: '', defaultEnd: '' },
    { key: 'so', label: 'So', defaultStart: '', defaultEnd: '' },
  ];
  const [localOption, setLocalOption] = useState(option);
  const localOptionRef = useRef(option);

  useEffect(() => {
    setLocalOption(option);
    localOptionRef.current = option;
  }, [option]);

  function parseTimeToMinutes(value) {
    const match = /^(\d{2}):(\d{2})$/.exec(String(value || '').trim());
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return (hours * 60) + minutes;
  }

  function formatHours(minutes) {
    if (!Number.isFinite(minutes) || minutes <= 0) return '0,00 h';
    return `${(minutes / 60).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;
  }

  function updateOption(next) {
    localOptionRef.current = next;
    setLocalOption(next);
    onChange(next);
  }

  return (
    <div className="card" style={{ marginBottom: '1rem', borderLeft: `4px solid ${localOption.farbe || '#22c55e'}` }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Name
          </label>
          <input
            type="text"
            value={localOption.name}
            onChange={(e) => updateOption({ ...localOption, name: e.target.value })}
            className="input"
            placeholder="z.B. Urlaub"
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Kurz
          </label>
          <input
            type="text"
            value={localOption.kurz}
            onChange={(e) => updateOption({ ...localOption, kurz: e.target.value })}
            className="input"
            placeholder="U"
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Farbe
          </label>
          <input
            type="color"
            value={localOption.farbe || '#22c55e'}
            onChange={(e) => updateOption({ ...localOption, farbe: e.target.value })}
            className="input"
            style={{ width: '80px', height: '40px', padding: '0.25rem' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Berechnung
          </label>
          <select
            value={localOption.berechnungstyp || 'FEHLZEIT'}
            onChange={(e) => updateOption({ ...localOption, berechnungstyp: e.target.value })}
            className="input"
          >
            <option value="FEHLZEIT">Fehlzeit</option>
            <option value="ARBEITSZEIT">Arbeitszeit</option>
          </select>
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '1rem' }}>
        <input
          type="checkbox"
          checked={localOption.aktiv ?? true}
          onChange={(e) => updateOption({ ...localOption, aktiv: e.target.checked })}
          style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
        />
        <span style={{ fontSize: '0.875rem', color: '#374151' }}>Aktiv</span>
      </label>

      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontWeight: '600', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
          Wochentage und Zeitfenster
        </div>
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {weekdayConfigs.map((day) => {
            const activeKey = `aktiv_${day.key}`;
            const startKey = `beginn_${day.key}`;
            const endKey = `ende_${day.key}`;
            const pauseKey = `pause_${day.key}`;
            const isActive = localOption[activeKey] ?? true;
            const startMinutes = parseTimeToMinutes(localOption[startKey] ?? day.defaultStart);
            const endMinutes = parseTimeToMinutes(localOption[endKey] ?? day.defaultEnd);
            const pauseMinutes = parseTimeToMinutes(localOption[pauseKey] ?? '') || 0;
            const nettoMinutes = (startMinutes != null && endMinutes != null) ? Math.max(0, endMinutes - startMinutes - pauseMinutes) : 0;
            return (
              <div
                key={day.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '90px 1fr 1fr 1fr 130px',
                  gap: '0.75rem',
                  alignItems: 'center',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  background: isActive ? '#ffffff' : '#f9fafb',
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}>
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => updateOption({ ...localOption, [activeKey]: e.target.checked })}
                    style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
                  />
                  {day.label}
                </label>
                <input
                  type="time"
                  value={localOption[startKey] ?? day.defaultStart}
                  onChange={(e) => updateOption({ ...localOption, [startKey]: e.target.value })}
                  className="input"
                  disabled={!isActive}
                />
                <input
                  type="time"
                  value={localOption[endKey] ?? day.defaultEnd}
                  onChange={(e) => updateOption({ ...localOption, [endKey]: e.target.value })}
                  className="input"
                  disabled={!isActive}
                />
                <input
                  type="time"
                  value={localOption[pauseKey] ?? ''}
                  onChange={(e) => updateOption({ ...localOption, [pauseKey]: e.target.value })}
                  className="input"
                  disabled={!isActive}
                />
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', textAlign: 'right' }}>
                  {formatHours(nettoMinutes)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button onClick={onCancel} className="btn">
          <X size={18} /> Abbrechen
        </button>
        <button
          onClick={() => onSave(localOptionRef.current)}
          className="btn btn-primary"
          disabled={!localOption.name || !localOption.kurz}
        >
          <Check size={18} /> Speichern
        </button>
      </div>
    </div>
  );
}

function TagOptionCard({ option, onEdit, onDelete }) {
  const weekdayLabels = [
    ['mo', 'Mo'],
    ['di', 'Di'],
    ['mi', 'Mi'],
    ['do', 'Do'],
    ['fr', 'Fr'],
    ['sa', 'Sa'],
    ['so', 'So'],
  ];
  const activeDaysText = weekdayLabels
    .filter(([key]) => option[`aktiv_${key}`] ?? true)
    .map(([, label]) => label)
    .join(', ');

  return (
    <div className="card" style={{ marginBottom: '1rem', borderLeft: `4px solid ${option.farbe || '#22c55e'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: '1rem',
            height: '1rem',
            background: option.farbe || '#22c55e',
            borderRadius: '0.25rem'
          }} />
          <div>
            <div style={{ fontWeight: '600' }}>
              {option.name} ({option.kurz})
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {option.berechnungstyp === 'ARBEITSZEIT' ? 'Arbeitszeit' : 'Fehlzeit'}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              Aktiv: {activeDaysText || 'Keine Tage'}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              Netto-Arbeitszeit wird automatisch berechnet
            </div>
            {!option.aktiv && (
              <div style={{ fontSize: '0.75rem', color: '#dc2626' }}>
                Inaktiv
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={onEdit} className="btn" title="Bearbeiten">
            Bearbeiten
          </button>
          <button
            onClick={onDelete}
            style={{ color: '#dc2626' }}
            className="btn"
            title="Löschen"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ArbeitszeitForm({ zeitraum, onChange, onSave, onCancel }) {
  return (
    <div className="card" style={{ marginBottom: '1rem', background: '#f9fafb' }}>
      <div style={{ display: 'grid', gap: '0.75rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
          <input
            type="number"
            min="1"
            max="31"
            value={zeitraum.von_tag}
            onChange={(e) => onChange({ ...zeitraum, von_tag: parseInt(e.target.value, 10) || 1 })}
            className="input"
            placeholder="Von Tag"
          />
          <input
            type="number"
            min="1"
            max="12"
            value={zeitraum.von_monat}
            onChange={(e) => onChange({ ...zeitraum, von_monat: parseInt(e.target.value, 10) || 1 })}
            className="input"
            placeholder="Von Monat"
          />
          <input
            type="number"
            min="1"
            max="31"
            value={zeitraum.bis_tag}
            onChange={(e) => onChange({ ...zeitraum, bis_tag: parseInt(e.target.value, 10) || 1 })}
            className="input"
            placeholder="Bis Tag"
          />
          <input
            type="number"
            min="1"
            max="12"
            value={zeitraum.bis_monat}
            onChange={(e) => onChange({ ...zeitraum, bis_monat: parseInt(e.target.value, 10) || 1 })}
            className="input"
            placeholder="Bis Monat"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <input
            type="number"
            step="0.25"
            value={zeitraum.max_mo_do}
            onChange={(e) => onChange({ ...zeitraum, max_mo_do: e.target.value })}
            className="input"
            placeholder="Max Mo-Do"
          />
          <input
            type="number"
            step="0.25"
            value={zeitraum.max_fr}
            onChange={(e) => onChange({ ...zeitraum, max_fr: e.target.value })}
            className="input"
            placeholder="Max Fr"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          <input
            type="text"
            value={zeitraum.beginn_mo_do}
            onChange={(e) => onChange({ ...zeitraum, beginn_mo_do: e.target.value })}
            className="input"
            placeholder="Beginn Mo-Do (HH:MM)"
          />
          <input
            type="text"
            value={zeitraum.ende_mo_do}
            onChange={(e) => onChange({ ...zeitraum, ende_mo_do: e.target.value })}
            className="input"
            placeholder="Ende Mo-Do (HH:MM)"
          />
          <input
            type="text"
            value={zeitraum.pause_mo_do}
            onChange={(e) => onChange({ ...zeitraum, pause_mo_do: e.target.value })}
            className="input"
            placeholder="Pause Mo-Do (HH:MM)"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          <input
            type="text"
            value={zeitraum.beginn_fr}
            onChange={(e) => onChange({ ...zeitraum, beginn_fr: e.target.value })}
            className="input"
            placeholder="Beginn Fr (HH:MM)"
          />
          <input
            type="text"
            value={zeitraum.ende_fr}
            onChange={(e) => onChange({ ...zeitraum, ende_fr: e.target.value })}
            className="input"
            placeholder="Ende Fr (HH:MM)"
          />
          <input
            type="text"
            value={zeitraum.pause_fr}
            onChange={(e) => onChange({ ...zeitraum, pause_fr: e.target.value })}
            className="input"
            placeholder="Pause Fr (HH:MM)"
          />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="btn">
            <X size={18} /> Abbrechen
          </button>
          <button onClick={onSave} className="btn btn-primary">
            <Check size={18} /> Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function ArbeitszeitCard({ zeitraum, onEdit, onDelete }) {
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: '600' }}>
            {String(zeitraum.von_tag).padStart(2, '0')}.{String(zeitraum.von_monat).padStart(2, '0')} - {String(zeitraum.bis_tag).padStart(2, '0')}.{String(zeitraum.bis_monat).padStart(2, '0')}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            Mo-Do max {zeitraum.max_mo_do}h | Fr max {zeitraum.max_fr}h
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={onEdit} className="btn">Bearbeiten</button>
          <button onClick={onDelete} className="btn" style={{ color: '#dc2626' }}>Löschen</button>
        </div>
      </div>
    </div>
  );
}

function StundenverrechnungForm({ eintrag, onChange, onSave, onCancel }) {
  return (
    <div className="card" style={{ marginBottom: '1rem', background: '#f9fafb' }}>
      <div style={{ display: 'grid', gap: '0.75rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <input
            type="date"
            value={eintrag.gueltig_ab || ''}
            onChange={(e) => onChange({ ...eintrag, gueltig_ab: e.target.value })}
            className="input"
          />
          <input
            type="number"
            step="0.01"
            value={eintrag.wert || ''}
            onChange={(e) => onChange({ ...eintrag, wert: e.target.value })}
            className="input"
            placeholder="Wert pro Stunde"
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="btn">
            <X size={18} /> Abbrechen
          </button>
          <button onClick={onSave} className="btn btn-primary">
            <Check size={18} /> Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function StundenverrechnungCard({ eintrag, onEdit, onDelete }) {
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: '600' }}>{eintrag.gueltig_ab}</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{Number(eintrag.wert || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR / Stunde</div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={onEdit} className="btn">Bearbeiten</button>
          <button onClick={onDelete} className="btn" style={{ color: '#dc2626' }}>Löschen</button>
        </div>
      </div>
    </div>
  );
}

function RechnungsaufschlagForm({ eintrag, onChange, onSave, onCancel }) {
  return (
    <div className="card" style={{ marginBottom: '1rem', background: '#f9fafb' }}>
      <div style={{ display: 'grid', gap: '0.75rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <input
            type="date"
            value={eintrag.gueltig_ab || ''}
            onChange={(e) => onChange({ ...eintrag, gueltig_ab: e.target.value })}
            className="input"
          />
          <input
            type="number"
            step="0.01"
            value={eintrag.wert || ''}
            onChange={(e) => onChange({ ...eintrag, wert: e.target.value })}
            className="input"
            placeholder="Aufschlag in %"
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="btn">
            <X size={18} /> Abbrechen
          </button>
          <button onClick={onSave} className="btn btn-primary">
            <Check size={18} /> Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function RechnungsaufschlagCard({ eintrag, onEdit, onDelete }) {
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: '600' }}>{eintrag.gueltig_ab}</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{Number(eintrag.wert || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={onEdit} className="btn">Bearbeiten</button>
          <button onClick={onDelete} className="btn" style={{ color: '#dc2626' }}>Löschen</button>
        </div>
      </div>
    </div>
  );
}

export default SettingsView;
